/**
 * PATH: apps/qa/src/components/camera-capture/useAudioRecording.ts
 * PURPOSE: Hook for voice memo recording with duration tracking
 */

import { useState, useRef, useCallback } from 'react'

export function useAudioRecording() {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [duration, setDuration] = useState(0)
  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const audioChunks = useRef<Blob[]>([])
  const startTime = useRef<number>(0)
  const timerRef = useRef<number | null>(null)

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaRecorder.current = new MediaRecorder(stream)
      audioChunks.current = []

      mediaRecorder.current.ondataavailable = (e) => {
        audioChunks.current.push(e.data)
      }

      mediaRecorder.current.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' })
        setAudioBlob(blob)
        stream.getTracks().forEach(t => t.stop())
      }

      mediaRecorder.current.start()
      setIsRecording(true)
      startTime.current = Date.now()
      
      timerRef.current = window.setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime.current) / 1000))
      }, 100)
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to start recording:', err)
    }
  }, [])

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop()
      setIsRecording(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isRecording])

  const clearRecording = useCallback(() => {
    setAudioBlob(null)
    setDuration(0)
  }, [])

  return { isRecording, audioBlob, duration, startRecording, stopRecording, clearRecording }
}
